/**
 * Insights Service
 * API calls for the Insights module
 */

import api from '@/services/api';
import { filenameFrom, triggerBlobDownload } from '@/utils/download';
import type {
  AgentStat,
  TenantRanking,
  TimeseriesPoint,
  TenantNode,
  InsightTemplate,
  TemplateAggregation,
  TemplateDashboardEntry,
  RetroactiveRun,
  CreateTemplatePayload,
  UpdateTemplatePayload,
  RunRequest,
  RunEstimate,
  TemplateDraft,
  TemplateSchedulePreview,
  InsightsByTenantRow,
  InsightsByAgentRow,
  TagTimeseries,
  TopicsOverview,
  TopicDrilldown,
  InsightsQuota,
  LibraryPage,
  LibraryReport,
  ReportDetail,
  ReportTemplate,
  ReportWindow,
  ReportTemplatePayload,
  RunNowPeriod,
  SchedulePreview,
} from '../types/insights';

/** An agent as GET /insights/chatbots lists it (with its avatar's colour and variant). */
export interface InsightsAgent {
  chatbot_id: string;
  chatbot_name: string;
  tenant_id: string;
  avatar_color?: string | null;
  avatar_variant?: number | null;
}

const BASE_URL = '/insights';

/**
 * Build a query string from an array of values using bracket notation
 * e.g. buildArrayParam('tenant_ids', ['a', 'b']) => 'tenant_ids[]=a&tenant_ids[]=b'
 */
function buildArrayParam(key: string, values: string[]): string {
  return values.map(v => `${encodeURIComponent(key)}[]=${encodeURIComponent(v)}`).join('&');
}

function buildQueryString(params: Record<string, string | number | string[] | undefined>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      if (value.length > 0) {
        parts.push(buildArrayParam(key, value));
      }
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

/**
 * Tenant scope → query params for the cost endpoints.
 *
 * Prefers the multi-select list (`tenant_ids[]`) so picking several tenants
 * aggregates across ALL of them; falls back to the legacy single `tenant_id`;
 * sends nothing when no tenant is chosen (the backend then defaults to the
 * caller's full visible scope — a group-admin's whole subtree, super-admin
 * platform-wide). Sending nothing is what makes the "no tenant" total ≥ any
 * single tenant's total.
 */
function tenantScopeParam(
  params: { tenantIds?: string[]; tenantId?: string },
): Record<string, string | string[]> {
  if (params.tenantIds && params.tenantIds.length > 0) {
    return { tenant_ids: params.tenantIds };
  }
  if (params.tenantId) {
    return { tenant_id: params.tenantId };
  }
  return {};
}

/** The Insights filter row as query parameters. */
export interface ScopeParams {
  date_from?: string;
  date_to?: string;
  tenant_ids?: string[];
  chatbot_ids?: string[];
}

interface DateRangeParams {
  date_from?: string;
  date_to?: string;
}

interface AgentStatsParams extends DateRangeParams {
  tenant_ids?: string[];
  chatbot_ids?: string[];
}

interface TimeseriesParams extends AgentStatsParams {}

interface TenantStatsParams extends DateRangeParams {
  sort_by?: string;
  tenant_ids?: string[];
  chatbot_ids?: string[];
}

const insightsService = {
  // ===========================================================================
  // Agent Stats
  // ===========================================================================

  /**
   * GET /insights/agents
   * Returns per-agent usage statistics
   */
  getAgentStats: async (params: AgentStatsParams = {}): Promise<AgentStat[]> => {
    const qs = buildQueryString({
      ...(params.date_from ? { date_from: params.date_from } : {}),
      ...(params.date_to ? { date_to: params.date_to } : {}),
      ...(params.tenant_ids ? { tenant_ids: params.tenant_ids } : {}),
      ...(params.chatbot_ids ? { chatbot_ids: params.chatbot_ids } : {}),
    });
    const response = await api.get<{ agents: AgentStat[] }>(`${BASE_URL}/agents${qs}`);
    return response.data.agents;
  },

  /**
   * GET /insights/agents/timeseries
   * Returns conversation timeseries data per agent
   */
  getAgentTimeseries: async (params: TimeseriesParams = {}): Promise<TimeseriesPoint[]> => {
    const qs = buildQueryString({
      ...(params.date_from ? { date_from: params.date_from } : {}),
      ...(params.date_to ? { date_to: params.date_to } : {}),
      ...(params.tenant_ids ? { tenant_ids: params.tenant_ids } : {}),
      ...(params.chatbot_ids ? { chatbot_ids: params.chatbot_ids } : {}),
    });
    const response = await api.get<{ timeseries: TimeseriesPoint[] }>(`${BASE_URL}/agents/timeseries${qs}`);
    return response.data.timeseries;
  },

  // ===========================================================================
  // Tenant Stats
  // ===========================================================================

  /**
   * GET /insights/tenants
   * Returns tenant ranking data
   */
  getTenantRankings: async (params: TenantStatsParams = {}): Promise<TenantRanking[]> => {
    const qs = buildQueryString({
      ...(params.date_from ? { date_from: params.date_from } : {}),
      ...(params.date_to ? { date_to: params.date_to } : {}),
      ...(params.sort_by ? { sort_by: params.sort_by } : {}),
      ...(params.tenant_ids ? { tenant_ids: params.tenant_ids } : {}),
      ...(params.chatbot_ids ? { chatbot_ids: params.chatbot_ids } : {}),
    });
    const response = await api.get<{ tenants: TenantRanking[] }>(`${BASE_URL}/tenants${qs}`);
    return response.data.tenants;
  },

  /**
   * GET /insights/tenants/timeseries
   * Returns timeseries data per tenant
   */
  getTenantTimeseries: async (params: TenantStatsParams = {}): Promise<TimeseriesPoint[]> => {
    const qs = buildQueryString({
      ...(params.date_from ? { date_from: params.date_from } : {}),
      ...(params.date_to ? { date_to: params.date_to } : {}),
      ...(params.tenant_ids ? { tenant_ids: params.tenant_ids } : {}),
      ...(params.chatbot_ids ? { chatbot_ids: params.chatbot_ids } : {}),
    });
    const response = await api.get<{ timeseries: TimeseriesPoint[] }>(`${BASE_URL}/tenants/timeseries${qs}`);
    return response.data.timeseries;
  },

  /**
   * GET /insights/tenants/tree
   * Returns the tenant tree for the scope selector
   */
  getTenantTree: async (): Promise<TenantNode[]> => {
    const response = await api.get<{ tenants: TenantNode[] }>(`${BASE_URL}/tenants/tree`);
    return response.data.tenants;
  },

  // ===========================================================================
  // Insight Templates
  // ===========================================================================

  /**
   * GET /insights/templates
   * Returns all insight templates for the current tenant
   */
  getTemplates: async (includeHidden = false): Promise<InsightTemplate[]> => {
    const qs = includeHidden ? '?include_hidden=true' : '';
    const response = await api.get<{ templates: InsightTemplate[] }>(`${BASE_URL}/templates${qs}`);
    return response.data.templates;
  },

  /**
   * POST /insights/templates
   * Creates a new insight template
   */
  createTemplate: async (payload: CreateTemplatePayload): Promise<InsightTemplate> => {
    const response = await api.post<{ template: InsightTemplate }>(`${BASE_URL}/templates`, payload);
    return response.data.template;
  },

  /**
   * PUT /insights/templates/:id
   * Updates an existing insight template
   */
  updateTemplate: async (templateId: string, payload: UpdateTemplatePayload): Promise<InsightTemplate> => {
    const response = await api.put<{ template: InsightTemplate }>(`${BASE_URL}/templates/${templateId}`, payload);
    return response.data.template;
  },

  /**
   * DELETE /insights/templates/:id
   * Deletes an insight template
   */
  deleteTemplate: async (templateId: string): Promise<void> => {
    await api.delete(`${BASE_URL}/templates/${templateId}`);
  },

  /**
   * GET /insights/templates/dashboard
   * Every template's aggregation for the filters, in one request (the dashboard).
   */
  getTemplatesDashboard: async (params: ScopeParams = {}): Promise<TemplateDashboardEntry[]> => {
    const qs = buildQueryString({ ...params });
    const response = await api.get<{ templates: TemplateDashboardEntry[] }>(`${BASE_URL}/templates/dashboard${qs}`);
    return response.data.templates;
  },

  /**
   * GET /insights/templates/:id/aggregations
   * Returns aggregated results for a template, optionally scoped by filters
   */
  getTemplateAggregations: async (
    templateId: string,
    params: { date_from?: string; date_to?: string; tenant_ids?: string[]; chatbot_ids?: string[] } = {}
  ): Promise<TemplateAggregation> => {
    const qs = buildQueryString({
      ...(params.date_from ? { date_from: params.date_from } : {}),
      ...(params.date_to ? { date_to: params.date_to } : {}),
      ...(params.tenant_ids ? { tenant_ids: params.tenant_ids } : {}),
      ...(params.chatbot_ids ? { chatbot_ids: params.chatbot_ids } : {}),
    });
    const response = await api.get<{ aggregation: Record<string, any> }>(`${BASE_URL}/templates/${templateId}/aggregations${qs}`);
    const raw = response.data.aggregation; // { total, distribution }
    return {
      template_id: templateId,
      response_type: 'label' as any,
      total_results: raw.total ?? 0,
      aggregation: raw,
      free_text_samples: raw.free_text_samples,
    } as TemplateAggregation;
  },

  // ===========================================================================
  // Chatbots
  // ===========================================================================

  /**
   * GET /insights/chatbots
   * Returns all chatbots within the caller's allowed scope
   */
  getChatbots: async (params: { tenant_ids?: string[] } = {}): Promise<InsightsAgent[]> => {
    const qs = buildQueryString({
      ...(params.tenant_ids ? { tenant_ids: params.tenant_ids } : {}),
    });
    const response = await api.get<{ chatbots: InsightsAgent[] }>(`${BASE_URL}/chatbots${qs}`);
    return response.data.chatbots;
  },

  // ===========================================================================
  // Retroactive Runs
  // ===========================================================================

  /**
   * POST /insights/retroactive-runs - run ONE template now: the newest
   * conversations it selects, up to the run's limit. The run is a row; nothing
   * runs in the request. Refusals carry an error_code (MANUAL_RUN_QUOTA,
   * SPEND_LIMIT, RUN_IN_PROGRESS, ORG_RUN_IN_PROGRESS, BAD_DATE_RANGE, ...).
   */
  startTemplateRun: async (req: RunRequest): Promise<RetroactiveRun> => {
    const response = await api.post<{ run: RetroactiveRun }>(`${BASE_URL}/retroactive-runs`, req);
    return response.data.run;
  },

  /** GET /insights/retroactive-runs/estimate - what that run would do and cost. */
  estimateTemplateRun: async (req: RunRequest): Promise<RunEstimate> => {
    const qs = buildQueryString({ ...req });
    const response = await api.get<RunEstimate>(`${BASE_URL}/retroactive-runs/estimate${qs}`);
    return response.data;
  },

  /** A run's progress. While it is active the request also works a short
   *  slice of it on the server: polling a run is what moves it along fastest. */
  getRetroactiveRun: async (runId: string): Promise<RetroactiveRun> => {
    const response = await api.get<{ run: RetroactiveRun }>(`${BASE_URL}/retroactive-runs/${runId}`);
    return response.data.run;
  },

  cancelRetroactiveRun: async (runId: string): Promise<RetroactiveRun> => {
    const response = await api.post<{ run: RetroactiveRun }>(`${BASE_URL}/retroactive-runs/${runId}/cancel`);
    return response.data.run;
  },

  /** POST /insights/templates/schedule-preview - the template's schedule, as
   *  the editor holds it: its next runs and what a run and a month would cost.
   *  Saves nothing. */
  previewTemplateSchedule: async (draft: TemplateDraft & {
    frequency: string; weekdays: number[]; month_day: number | null; run_time: string;
  }): Promise<TemplateSchedulePreview> => {
    const response = await api.post<TemplateSchedulePreview>(`${BASE_URL}/templates/schedule-preview`, draft);
    return response.data;
  },

  /** GET /insights/templates/:id/answers.csv - a template's answers, conversation by conversation, as a file. */
  downloadTemplateAnswers: async (
    templateId: string,
    params: ScopeParams & { value?: string },
    fallbackName: string,
  ): Promise<{ blob: Blob; fileName: string }> => {
    const qs = buildQueryString({ ...params });
    const response = await api.get(`${BASE_URL}/templates/${templateId}/answers.csv${qs}`, { responseType: 'blob' });
    return { blob: response.data as Blob, fileName: filenameFrom(response.headers, fallbackName) };
  },

  // ===========================================================================
  // Insights-by-tenant / Insights-by-agent
  // ===========================================================================

  getInsightsByTenant: async (params: AgentStatsParams = {}): Promise<InsightsByTenantRow[]> => {
    const qs = buildQueryString({
      ...(params.date_from ? { date_from: params.date_from } : {}),
      ...(params.date_to ? { date_to: params.date_to } : {}),
      ...(params.tenant_ids ? { tenant_ids: params.tenant_ids } : {}),
    });
    const response = await api.get<{ insights: InsightsByTenantRow[] }>(`${BASE_URL}/insights-by-tenant${qs}`);
    return response.data.insights;
  },

  getInsightsByAgent: async (params: AgentStatsParams = {}): Promise<InsightsByAgentRow[]> => {
    const qs = buildQueryString({
      ...(params.date_from ? { date_from: params.date_from } : {}),
      ...(params.date_to ? { date_to: params.date_to } : {}),
      ...(params.tenant_ids ? { tenant_ids: params.tenant_ids } : {}),
      ...(params.chatbot_ids ? { chatbot_ids: params.chatbot_ids } : {}),
    });
    const response = await api.get<{ insights: InsightsByAgentRow[] }>(`${BASE_URL}/insights-by-agent${qs}`);
    return response.data.insights;
  },

  /**
   * GET /insights/templates/:id/tag-timeseries
   * Weekly tag frequency timeseries for auto-topic (multi_label with no options) templates
   */
  getTagTimeseries: async (
    templateId: string,
    params: { date_from?: string; date_to?: string; tenant_ids?: string[]; chatbot_ids?: string[]; top_n?: number } = {}
  ): Promise<TagTimeseries> => {
    const qs = buildQueryString({
      ...(params.date_from ? { date_from: params.date_from } : {}),
      ...(params.date_to ? { date_to: params.date_to } : {}),
      ...(params.tenant_ids ? { tenant_ids: params.tenant_ids } : {}),
      ...(params.chatbot_ids ? { chatbot_ids: params.chatbot_ids } : {}),
      ...(params.top_n != null ? { top_n: params.top_n } : {}),
    });
    const response = await api.get<TagTimeseries>(`${BASE_URL}/templates/${templateId}/tag-timeseries${qs}`);
    return response.data;
  },

  /**
   * GET /insights/topics/overview
   * Returns aggregated topic distribution and timeseries across all auto-topic templates
   */
  getTopicsOverview: async (params: { date_from?: string; date_to?: string; tenant_ids?: string[]; chatbot_ids?: string[]; top_n?: number; template_id?: string } = {}): Promise<TopicsOverview> => {
    const qs = buildQueryString({
      ...(params.date_from ? { date_from: params.date_from } : {}),
      ...(params.date_to ? { date_to: params.date_to } : {}),
      ...(params.tenant_ids ? { tenant_ids: params.tenant_ids } : {}),
      ...(params.chatbot_ids ? { chatbot_ids: params.chatbot_ids } : {}),
      ...(params.top_n != null ? { top_n: params.top_n } : {}),
      ...(params.template_id ? { template_id: params.template_id } : {}),
    });
    const response = await api.get<TopicsOverview>(`${BASE_URL}/topics/overview${qs}`);
    return response.data;
  },

  /**
   * GET /insights/topics/drilldown
   */
  getTopicDrilldown: async (tag: string, params: { date_from?: string; date_to?: string; tenant_ids?: string[]; chatbot_ids?: string[]; template_id?: string } = {}): Promise<TopicDrilldown> => {
    const qs = buildQueryString({
      tag,
      ...(params.date_from ? { date_from: params.date_from } : {}),
      ...(params.date_to ? { date_to: params.date_to } : {}),
      ...(params.tenant_ids ? { tenant_ids: params.tenant_ids } : {}),
      ...(params.chatbot_ids ? { chatbot_ids: params.chatbot_ids } : {}),
      ...(params.template_id ? { template_id: params.template_id } : {}),
    });
    const response = await api.get<TopicDrilldown>(`${BASE_URL}/topics/drilldown${qs}`);
    return response.data;
  },

  /**
   * GET /insights/quota
   * Returns the current user's quota usage
   */
  getQuotaUsage: async (): Promise<InsightsQuota> => {
    const response = await api.get<InsightsQuota>(`${BASE_URL}/quota`);
    return response.data;
  },

  // -------------------------------------------------------------------------
  // Cost monitoring — backed by llm_call_events ledger
  // -------------------------------------------------------------------------

  /**
   * GET /insights/cost/by-component — total + per-component breakdown.
   *
   * - `includeDescendants=true` expands the tenant filter to cover the
   *    full sub-tree (parent + every descendant).
   * - `chatbotIds` (when set) sends `chatbot_ids[]` so the backend can
   *    filter by multiple agents at once. Falls back to `chatbotId` when
   *    you have only one.
   */
  getCostByComponent: async (params: {
    dateFrom?: string;
    dateTo?: string;
    tenantIds?: string[];
    chatbotId?: string;
    chatbotIds?: string[];
    includeDescendants?: boolean;
  } = {}): Promise<CostByComponent> => {
    const qs = buildQueryString({
      ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
      ...(params.dateTo ? { date_to: params.dateTo } : {}),
      ...tenantScopeParam(params),
      ...(params.chatbotIds && params.chatbotIds.length > 0
        ? { chatbot_ids: params.chatbotIds }
        : params.chatbotId
          ? { chatbot_id: params.chatbotId }
          : {}),
      ...(params.includeDescendants ? { include_descendants: '1' } : {}),
    });
    const response = await api.get<CostByComponent>(`${BASE_URL}/cost/by-component${qs}`);
    return response.data;
  },

  /**
   * GET /insights/cost/by-call-kind — chat vs embedding vs title vs summary etc.
   */
  getCostByCallKind: async (params: {
    dateFrom?: string;
    dateTo?: string;
    tenantId?: string;
    chatbotIds?: string[];
  } = {}): Promise<CostByCallKindResponse> => {
    const qs = buildQueryString({
      ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
      ...(params.dateTo ? { date_to: params.dateTo } : {}),
      ...tenantScopeParam(params),
    });
    // chatbot_ids[] cannot go through buildQueryString without losing array
    // semantics — append manually after the base qs.
    const extra = (params.chatbotIds || [])
      .map((id) => `chatbot_ids[]=${encodeURIComponent(id)}`)
      .join('&');
    // qs already starts with '?'; strip it before joining with extra so
    // the final URL has exactly ONE '?' separator. The earlier double-'?'
    // bug (every cost endpoint was sending `??date_from=...`) made Flask
    // treat the query as having NO date_from at all and silently
    // fall back to the 30-day default — every cost chart was date-blind.
    const fullQs = [qs.replace(/^\?/, ''), extra].filter(Boolean).join('&');
    const response = await api.get<CostByCallKindResponse>(`${BASE_URL}/cost/by-call-kind${fullQs ? `?${fullQs}` : ''}`);
    return response.data;
  },

  /**
   * GET /insights/cost/by-tool — which ADK tools burn the most tokens.
   * Supports multi-agent filter via `chatbotIds`.
   */
  getCostByTool: async (params: {
    dateFrom?: string;
    dateTo?: string;
    tenantId?: string;
    chatbotId?: string;
    chatbotIds?: string[];
  } = {}): Promise<CostByToolResponse> => {
    const qs = buildQueryString({
      ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
      ...(params.dateTo ? { date_to: params.dateTo } : {}),
      ...tenantScopeParam(params),
      ...(params.chatbotIds && params.chatbotIds.length > 0
        ? { chatbot_ids: params.chatbotIds }
        : params.chatbotId
          ? { chatbot_id: params.chatbotId }
          : {}),
    });
    const response = await api.get<CostByToolResponse>(`${BASE_URL}/cost/by-tool${qs}`);
    return response.data;
  },

  /**
   * GET /insights/cost/provider-rollup — invoice-shape rollup.
   * Compare this side-by-side with your credit-card invoice line items.
   */
  getProviderRollup: async (params: {
    dateFrom?: string;
    dateTo?: string;
    tenantIds?: string[];
    chatbotIds?: string[];
    includeDescendants?: boolean;
  } = {}): Promise<ProviderRollupResponse> => {
    const qs = buildQueryString({
      ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
      ...(params.dateTo ? { date_to: params.dateTo } : {}),
      ...tenantScopeParam(params),
      ...(params.chatbotIds && params.chatbotIds.length > 0
        ? { chatbot_ids: params.chatbotIds }
        : {}),
      ...(params.includeDescendants ? { include_descendants: '1' } : {}),
    });
    const response = await api.get<ProviderRollupResponse>(`${BASE_URL}/cost/provider-rollup${qs}`);
    return response.data;
  },

  /**
   * GET /insights/cost/by-api-key — which API key served each dollar,
   * per key × provider × day. `api_key_id = null` rows were served by the
   * platform env key. Drives the "By API key" panel that makes a key
   * switchover visible as a color change in the daily chart.
   */
  getCostByApiKey: async (params: {
    dateFrom?: string;
    dateTo?: string;
    tenantIds?: string[];
    chatbotIds?: string[];
    includeDescendants?: boolean;
    /** Narrow to one key. UUID for a tenant key, 'platform' for the env key. */
    apiKeyId?: string;
  } = {}): Promise<CostByApiKeyResponse> => {
    const qs = buildQueryString({
      ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
      ...(params.dateTo ? { date_to: params.dateTo } : {}),
      ...tenantScopeParam(params),
      ...(params.chatbotIds && params.chatbotIds.length > 0
        ? { chatbot_ids: params.chatbotIds }
        : {}),
      ...(params.includeDescendants ? { include_descendants: '1' } : {}),
      ...(params.apiKeyId ? { api_key_id: params.apiKeyId } : {}),
    });
    const response = await api.get<CostByApiKeyResponse>(`${BASE_URL}/cost/by-api-key${qs}`);
    return response.data;
  },

  /**
   * GET /insights/cost/api-keys — the key catalogue for the super-admin
   * picker: every tenant key with its label, masked hint, provider and the
   * tenant it serves, plus one synthetic platform-key entry per provider.
   * SUPER-ADMIN ONLY (403 for every other role).
   */
  getApiKeyCatalogue: async (): Promise<{ keys: ApiKeyCatalogueEntry[] }> => {
    const response = await api.get<{ keys: ApiKeyCatalogueEntry[] }>(
      `${BASE_URL}/cost/api-keys`,
    );
    return response.data;
  },

  /**
   * GET /insights/cost/by-tenant — cost per tenant inside the current scope.
   * Drives the per-sub-tenant table when a parent + descendants scope is on.
   */
  getCostByTenant: async (params: {
    dateFrom?: string;
    dateTo?: string;
    tenantIds?: string[];
    chatbotIds?: string[];
    includeDescendants?: boolean;
  } = {}): Promise<CostByTenantResponse> => {
    const qs = buildQueryString({
      ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
      ...(params.dateTo ? { date_to: params.dateTo } : {}),
      ...tenantScopeParam(params),
      ...(params.chatbotIds && params.chatbotIds.length > 0
        ? { chatbot_ids: params.chatbotIds }
        : {}),
      ...(params.includeDescendants ? { include_descendants: '1' } : {}),
    });
    const response = await api.get<CostByTenantResponse>(`${BASE_URL}/cost/by-tenant${qs}`);
    return response.data;
  },

  /**
   * GET /insights/cost/pricing-versions — data-accuracy check. When
   * stale_call_count > 0, part of the displayed spend was priced under a
   * superseded pricing config and history should be repriced.
   */
  getPricingVersions: async (params: {
    dateFrom?: string;
    dateTo?: string;
    tenantIds?: string[];
    chatbotIds?: string[];
    includeDescendants?: boolean;
  } = {}): Promise<PricingVersionsResponse> => {
    const qs = buildQueryString({
      ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
      ...(params.dateTo ? { date_to: params.dateTo } : {}),
      ...tenantScopeParam(params),
      ...(params.chatbotIds && params.chatbotIds.length > 0
        ? { chatbot_ids: params.chatbotIds }
        : {}),
      ...(params.includeDescendants ? { include_descendants: '1' } : {}),
    });
    const response = await api.get<PricingVersionsResponse>(`${BASE_URL}/cost/pricing-versions${qs}`);
    return response.data;
  },

  /**
   * GET /insights/cost/artifacts — artifacts (decks/docs/pages/forms) agents
   * produced, by kind, with ATTRIBUTED cost (the chat turns that built them;
   * artifacts make no LLM call of their own). Drives the Artifacts panel.
   */
  getArtifacts: async (params: {
    dateFrom?: string;
    dateTo?: string;
    tenantIds?: string[];
    chatbotIds?: string[];
    includeDescendants?: boolean;
  } = {}): Promise<ArtifactsSummaryResponse> => {
    const qs = buildQueryString({
      ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
      ...(params.dateTo ? { date_to: params.dateTo } : {}),
      ...tenantScopeParam(params),
      ...(params.chatbotIds && params.chatbotIds.length > 0
        ? { chatbot_ids: params.chatbotIds }
        : {}),
      ...(params.includeDescendants ? { include_descendants: '1' } : {}),
    });
    const response = await api.get<ArtifactsSummaryResponse>(`${BASE_URL}/cost/artifacts${qs}`);
    return response.data;
  },

  /**
   * GET /insights/cost/daily-timeseries — per-day cost + tokens for the
   * selected filter set. Drives the two daily charts on Cost Breakdown.
   */
  getDailyTimeseries: async (params: {
    dateFrom?: string;
    dateTo?: string;
    tenantIds?: string[];
    chatbotIds?: string[];
    includeDescendants?: boolean;
  } = {}): Promise<DailyTimeseriesResponse> => {
    const qs = buildQueryString({
      ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
      ...(params.dateTo ? { date_to: params.dateTo } : {}),
      ...tenantScopeParam(params),
      ...(params.chatbotIds && params.chatbotIds.length > 0
        ? { chatbot_ids: params.chatbotIds }
        : {}),
      ...(params.includeDescendants ? { include_descendants: '1' } : {}),
    });
    const response = await api.get<DailyTimeseriesResponse>(
      `${BASE_URL}/cost/daily-timeseries${qs}`,
    );
    return response.data;
  },

  /**
   * GET /insights/cost/conversations-timeseries — one point per conversation
   * for the selected agent: (created_at, total_cost_usd). Drives the
   * "Cost per conversation over time" scatter on the Cost Breakdown tab.
   * Privacy: no titles returned — only opaque short ids.
   */
  getConversationsCostTimeseries: async (params: {
    dateFrom?: string;
    dateTo?: string;
    tenantId?: string;
    tenantIds?: string[];
    chatbotIds: string[];
  }): Promise<ConversationsCostTimeseriesResponse> => {
    const qs = buildQueryString({
      ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
      ...(params.dateTo ? { date_to: params.dateTo } : {}),
      ...tenantScopeParam(params),
    });
    const extra = (params.chatbotIds || [])
      .map((id) => `chatbot_ids[]=${encodeURIComponent(id)}`)
      .join('&');
    const fullQs = [qs.replace(/^\?/, ''), extra].filter(Boolean).join('&');
    const response = await api.get<ConversationsCostTimeseriesResponse>(
      `${BASE_URL}/cost/conversations-timeseries${fullQs ? `?${fullQs}` : ''}`,
    );
    return response.data;
  },

  /**
   * GET /insights/cost/per-conversation-averages — average cost + tokens
   * per conversation for the selected filter set. Bridge between real
   * usage and simulator projections.
   */
  getPerConversationAverages: async (params: {
    dateFrom?: string;
    dateTo?: string;
    tenantIds?: string[];
    chatbotIds?: string[];
    includeDescendants?: boolean;
  } = {}): Promise<PerConversationAveragesResponse> => {
    const qs = buildQueryString({
      ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
      ...(params.dateTo ? { date_to: params.dateTo } : {}),
      ...tenantScopeParam(params),
      ...(params.chatbotIds && params.chatbotIds.length > 0
        ? { chatbot_ids: params.chatbotIds }
        : {}),
      ...(params.includeDescendants ? { include_descendants: '1' } : {}),
    });
    const response = await api.get<PerConversationAveragesResponse>(
      `${BASE_URL}/cost/per-conversation-averages${qs}`,
    );
    return response.data;
  },

  /**
   * GET /insights/messages/:id/breakdown — full per-iteration drilldown.
   */
  getMessageBreakdown: async (messageId: string): Promise<MessageBreakdownResponse> => {
    const response = await api.get<MessageBreakdownResponse>(`${BASE_URL}/messages/${messageId}/breakdown`);
    return response.data;
  },

  /**
   * GET /insights/conversations/recent — recent conversations for the picker.
   */
  getRecentConversations: async (params: {
    tenantId?: string;
    chatbotIds?: string[];
    dateFrom?: string;
    dateTo?: string;
  } = {}): Promise<RecentConversationsResponse> => {
    const qs = buildQueryString({
      ...tenantScopeParam(params),
      ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
      ...(params.dateTo ? { date_to: params.dateTo } : {}),
    });
    // chatbot_ids[] are array-valued — appended manually
    const extra = (params.chatbotIds || [])
      .map((id) => `chatbot_ids[]=${encodeURIComponent(id)}`)
      .join('&');
    const fullQs = [qs.replace(/^\?/, ''), extra].filter(Boolean).join('&');
    const response = await api.get<RecentConversationsResponse>(
      `${BASE_URL}/conversations/recent${fullQs ? `?${fullQs}` : ''}`,
    );
    return response.data;
  },

  /**
   * GET /insights/conversations/:id/timeline — per-conversation cost timeline.
   */
  getConversationTimeline: async (conversationId: string, params: { tenantId?: string } = {}): Promise<ConversationTimelineResponse> => {
    const qs = buildQueryString({
      ...(params.tenantId ? { tenant_id: params.tenantId } : {}),
    });
    const response = await api.get<ConversationTimelineResponse>(`${BASE_URL}/conversations/${conversationId}/timeline${qs}`);
    return response.data;
  },

  /**
   * GET /insights/conversations/:id/summary — one-line totals for a conversation.
   */
  getConversationSummary: async (conversationId: string, params: { tenantId?: string } = {}): Promise<ConversationSummaryResponse> => {
    const qs = buildQueryString({
      ...(params.tenantId ? { tenant_id: params.tenantId } : {}),
    });
    const response = await api.get<ConversationSummaryResponse>(`${BASE_URL}/conversations/${conversationId}/summary${qs}`);
    return response.data;
  },

  // ---- Reports (item 63) -------------------------------------------------------

  listReports: async (params: {
    templateId?: string; unread?: boolean; q?: string; since?: string; until?: string; limit?: number; offset?: number;
  } = {}): Promise<LibraryPage> => {
    // The title search goes in the body, never the URL (the platform's request
    // log keeps every URL); the filters stay in the query string.
    const response = await api.post<LibraryPage>(`${BASE_URL}/reports`, { q: params.q || '' }, {
      params: {
        template_id: params.templateId || undefined, unread: params.unread ? 1 : undefined,
        since: params.since || undefined, until: params.until || undefined,
        limit: params.limit, offset: params.offset,
      },
    });
    return response.data;
  },

  /** Opens a report (and marks it read, with its bell item). */
  openReport: async (reportId: string): Promise<ReportDetail> => {
    const response = await api.get<{ report: ReportDetail }>(`${BASE_URL}/reports/${reportId}`);
    return response.data.report;
  },

  markReportUnread: async (reportId: string): Promise<void> => {
    await api.post(`${BASE_URL}/reports/${reportId}/unread`);
  },

  /** Delete one of the person's reports (its bell notice goes with it). */
  deleteReport: async (reportId: string): Promise<void> => {
    await api.delete(`${BASE_URL}/reports/${reportId}`);
  },

  /** The report as a file (PDF and Word in the export's plain layout, or its
   *  Markdown), saved under the name the server gives it. */
  downloadReport: async (reportId: string, format: 'pdf' | 'docx' | 'md'): Promise<void> => {
    const response = await api.get(`${BASE_URL}/reports/${reportId}/download`, {
      params: { format }, responseType: 'blob',
    });
    triggerBlobDownload(response.data, filenameFrom(response.headers, `report.${format}`));
  },

  /** The days each fixed "Run a report now" choice covers today, in the owner's zone. */
  reportRunWindows: async (): Promise<Partial<Record<RunNowPeriod, ReportWindow>>> => {
    const response = await api.get<{ windows: Partial<Record<RunNowPeriod, ReportWindow>> }>(
      `${BASE_URL}/reports/run-windows`);
    return response.data.windows || {};
  },

  listReportTemplates: async (): Promise<ReportTemplate[]> => {
    const response = await api.get<{ templates: ReportTemplate[] }>(`${BASE_URL}/reports/templates`);
    return response.data.templates || [];
  },

  createReportTemplate: async (payload: ReportTemplatePayload): Promise<ReportTemplate> => {
    const response = await api.post<{ template: ReportTemplate }>(`${BASE_URL}/reports/templates`, payload);
    return response.data.template;
  },

  updateReportTemplate: async (templateId: string, payload: ReportTemplatePayload): Promise<ReportTemplate> => {
    const response = await api.patch<{ template: ReportTemplate }>(`${BASE_URL}/reports/templates/${templateId}`,
      payload);
    return response.data.template;
  },

  deleteReportTemplate: async (templateId: string): Promise<void> => {
    await api.delete(`${BASE_URL}/reports/templates/${templateId}`);
  },

  setReportTemplatePaused: async (templateId: string, paused: boolean): Promise<ReportTemplate> => {
    const response = await api.post<{ template: ReportTemplate }>(
      `${BASE_URL}/reports/templates/${templateId}/${paused ? 'pause' : 'resume'}`);
    return response.data.template;
  },

  runReportTemplate: async (templateId: string, body: { period: RunNowPeriod; start?: string; end?: string }):
    Promise<LibraryReport> => {
    const response = await api.post<{ report: LibraryReport }>(`${BASE_URL}/reports/templates/${templateId}/run`,
      body);
    return response.data.report;
  },

  previewReportSchedule: async (payload: ReportTemplatePayload & { report_template_id?: string }):
    Promise<SchedulePreview> => {
    const response = await api.post<SchedulePreview>(`${BASE_URL}/reports/schedule-preview`, payload);
    return response.data;
  },
};

// ---------------------------------------------------------------------------
// Per-conversation timeline types
// ---------------------------------------------------------------------------

/** Cache-state classification per turn — keep in sync with backend cache_state.py */
export type CacheStateLabel =
  | 'cold_start'
  | 'cache_hit'
  | 'cache_lost_switch'
  | 'cache_warming'
  | 'cache_rewrite'
  | 'cache_expired'
  | 'no_cache'
  | null;

export interface ConversationTurnEvent extends LlmCallEventDto {
  cache_state: CacheStateLabel;
  cache_explanation: string | null;
  switched_from: string | null;
  switched_to: string | null;
  /**
   * For tool events (embedding/rag_rerank/web_search_grounding/file_enrichment):
   * the input-token delta + USD cost this tool TRIGGERED on the next chat
   * iteration. A knowledge_search whose embedding costs $0.000003 may have
   * injected 4.7K context tokens that cost $0.014 of input on the chat
   * call that consumed them. That cost is captured here, attributed to
   * the tool — instead of being silently rolled into the chat iteration.
   * 0 for chat events and for background-only events (title/summary/insight).
   */
  downstream_input_tokens: number;
  downstream_cost_usd: number;
}

export interface ConversationTurn {
  message_id: string;
  created_at: string | null;
  provider: string | null;
  model_name: string | null;
  cache_state: CacheStateLabel;
  cache_explanation: string | null;
  switched_from: string | null;
  switched_to: string | null;
  iteration_count: number;
  tool_call_count: number;
  input_tokens: number;
  output_tokens: number;
  reasoning_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  total_cost_usd: number;
  events: ConversationTurnEvent[];
}

export interface ConversationTimelineResponse {
  conversation_id: string;
  tenant_id: string;
  chatbot_id: string | null;
  turns: ConversationTurn[];
  total_turns: number;
  event_count: number;
}

export interface ModelUsed {
  provider: string;
  model: string;
}

export interface ConversationSummaryResponse {
  conversation_id: string;
  chatbot_id: string | null;
  conversation_title: string | null;
  first_event_at: string | null;
  last_event_at: string | null;
  call_count: number;
  input_tokens: number;
  output_tokens: number;
  reasoning_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  total_cost_usd: number;
  cache_savings_pct: number;
  models_used: ModelUsed[];
  model_switch_count: number;
}

export interface RecentConversation {
  conversation_id: string;
  conversation_title: string | null;
  chatbot_name: string | null;
  created_at: string | null;
  total_cost_usd: number;
  event_count: number;
}

export interface RecentConversationsResponse {
  tenant_id: string;
  conversations: RecentConversation[];
}

// ---------------------------------------------------------------------------
// Cost monitoring response types
// ---------------------------------------------------------------------------

export interface CostByComponent {
  date_from: string;
  date_to: string;
  tenant_id: string | null;
  tenant_ids?: string[] | null;
  chatbot_id: string | null;
  cost_breakdown: {
    input_cost_usd: number;
    output_cost_usd: number;
    cache_read_cost_usd: number;
    cache_write_cost_usd: number;
    /** Server-side web search, billed per request (Anthropic: $10 / 1,000). */
    web_search_cost_usd: number;
    total_cost_usd: number;
  };
  token_breakdown: {
    input_tokens: number;
    output_tokens: number;
    reasoning_tokens: number;
    cache_read_tokens: number;
    cache_creation_tokens: number;
  };
  cache_savings_pct: number;
  reasoning_pct_of_output: number;
  call_count: number;
  /** Part of `cost_breakdown.total_cost_usd`, not added to it: spend recorded
   *  in conversations deleted since. A deleted conversation keeps its cost rows
   *  (the ledger holds its id as a tombstone), so the total still matches the
   *  bill and this says how much of it came from conversations no longer there. */
  deleted_conversations?: {
    calls: number;
    cost_usd: number;
    conversations: number;
  };
  /** What to DISPLAY so the page never promises less than the invoice.
   *  `cost_breakdown.total_cost_usd` is what the ledger measured; this adds a
   *  configured uplift for spend the ledger structurally cannot see (calls
   *  dropped for want of a tenant_id, per-request web-search fees). */
  invoice_safety?: {
    enabled: boolean;
    total_pct: number;
    untracked_call_pct?: number;
    non_token_pct?: number;
    measured_cost_usd: number;
    safety_uplift_usd: number;
    invoice_safe_cost_usd: number;
    basis?: string;
  };
  /** Response-time breakdown (milliseconds). Null when no rows match.
   *  avg_first_token_ms / p50_first_token_ms / p95_first_token_ms — when
   *  the AI started streaming (TTFT). avg_duration_ms / p50 / p95 — when
   *  the response was complete. */
  response_time?: {
    avg_first_token_ms: number | null;
    p50_first_token_ms: number | null;
    p95_first_token_ms: number | null;
    avg_duration_ms: number | null;
    p50_duration_ms: number | null;
    p95_duration_ms: number | null;
  };
}

export interface CallKindToolRow {
  /** ADK tool that triggered this call, or "(main loop)" for the agent's own planning calls. */
  tool_name: string;
  total_cost_usd: number;
  input_tokens: number;
  output_tokens: number;
  call_count: number;
}

export interface CallKindRow {
  call_kind: string;
  total_cost_usd: number;
  input_tokens: number;
  output_tokens: number;
  call_count: number;
  /**
   * Per-tool breakdown WITHIN this call_kind. Lets the UI expand
   * "chat: $0.12" into "  (main loop): $0.10 · knowledge_search: $0.02".
   */
  tools: CallKindToolRow[];
}

export interface CostByCallKindResponse {
  date_from: string;
  date_to: string;
  tenant_id: string;
  breakdown: CallKindRow[];
}

export interface ToolRow {
  tool_name: string;
  total_cost_usd: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  call_count: number;
}

export interface CostByToolResponse {
  date_from: string;
  date_to: string;
  tenant_id: string;
  chatbot_id: string | null;
  tools: ToolRow[];
}

export interface ProviderRollupRow {
  provider: string;
  model_name: string;
  day: string;
  input_tokens: number;
  output_tokens: number;
  reasoning_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  input_cost_usd: number;
  output_cost_usd: number;
  cache_read_cost_usd: number;
  cache_write_cost_usd: number;
  total_cost_usd: number;
}

export interface ProviderRollupResponse {
  date_from: string;
  date_to: string;
  tenant_id: string | null;
  rows: ProviderRollupRow[];
}

/** One row per API key × provider × day. `api_key_id = null` = platform env key. */
export interface ApiKeyCostRow {
  api_key_id: string | null;
  /** Which key served the row: the platform env key (no id), a registered
   *  tenant key, or one deleted since — whose spend stays its own. */
  key_state: 'platform' | 'registered' | 'deleted';
  provider: string;
  /** Admin label of the tenant key; null for the platform env key. */
  key_name: string | null;
  /** Masked hint (last 4 chars); null for the platform env key. */
  key_hint: string | null;
  day: string;
  input_cost_usd: number;
  output_cost_usd: number;
  cache_read_cost_usd: number;
  cache_write_cost_usd: number;
  total_cost_usd: number;
  call_count: number;
  first_call_at: string | null;
  last_call_at: string | null;
}

export interface CostByApiKeyResponse {
  date_from: string;
  date_to: string;
  tenant_id: string | null;
  api_key_id?: string | null;
  rows: ApiKeyCostRow[];
}

/** One selectable key in the super-admin picker. */
export interface ApiKeyCatalogueEntry {
  /** null for the platform env key — pass 'platform' to filter by it. */
  api_key_id: string | null;
  key_name: string | null;
  key_hint: string | null;
  provider: string;
  /** Which tenant owns the key; null for the platform key. */
  tenant_id: string | null;
  tenant_name: string | null;
  /** 'subtree' keys also serve descendant tenants; 'platform' = env key. */
  key_scope: string;
  key_status: string;
  last_used_at: string | null;
  is_platform_key: boolean;
}

export interface TenantCostRow {
  tenant_id: string | null;
  tenant_name: string | null;
  /** The tenant was deleted; its spend stays under its id (no name left). */
  tenant_deleted: boolean;
  /** The unattributed bucket: billed calls that reached the ledger with no tenant. */
  unattributed: boolean;
  input_cost_usd: number;
  output_cost_usd: number;
  cache_read_cost_usd: number;
  cache_write_cost_usd: number;
  total_cost_usd: number;
  call_count: number;
  conversation_count: number;
}

export interface CostByTenantResponse {
  date_from: string;
  date_to: string;
  tenant_id: string | null;
  rows: TenantCostRow[];
}

export interface PricingVersionRow {
  pricing_version: string;
  total_cost_usd: number;
  call_count: number;
  first_call_at: string | null;
  last_call_at: string | null;
  is_current: boolean;
}

export interface PricingVersionsResponse {
  date_from: string;
  date_to: string;
  tenant_id: string | null;
  current_version: string;
  rows: PricingVersionRow[];
  stale_call_count: number;
  stale_cost_usd: number;
}

export interface ArtifactFormatRow {
  format: string;
  artifacts: number;
  edits: number;
  producing_turns: number;
  cost_usd: number;
}

export interface ArtifactsSummaryResponse {
  date_from: string;
  date_to: string;
  tenant_id: string | null;
  by_format: ArtifactFormatRow[];
  total_artifacts: number;
  total_edits: number;
  total_cost_usd: number;
}

export interface DailyTimeseriesRow {
  day: string;
  input_tokens: number;
  output_tokens: number;
  reasoning_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  input_cost_usd: number;
  output_cost_usd: number;
  cache_read_cost_usd: number;
  cache_write_cost_usd: number;
  total_cost_usd: number;
  call_count: number;
  conversation_count: number;
}

export interface DailyTimeseriesResponse {
  date_from: string;
  date_to: string;
  tenant_ids: string[] | null;
  chatbot_ids: string[];
  days: DailyTimeseriesRow[];
}

/** Per-conversation cost point — drives the "Cost per conversation
 *  over time" scatter chart on the Cost Breakdown tab. */
export interface ConversationCostPoint {
  conversation_id_short: string;
  created_at: string;
  total_cost_usd: number;
  event_count: number;
}

export interface ConversationsCostTimeseriesResponse {
  date_from: string;
  date_to: string;
  chatbot_ids: string[];
  conversations: ConversationCostPoint[];
}

export interface PerConversationAveragesResponse {
  date_from: string;
  date_to: string;
  tenant_ids: string[] | null;
  chatbot_ids: string[];
  conversation_count: number;
  avg_input_tokens: number;
  avg_output_tokens: number;
  avg_reasoning_tokens: number;
  avg_cache_read_tokens: number;
  avg_cache_creation_tokens: number;
  avg_total_cost_usd: number;
  avg_calls_per_conversation: number;
  cache_hit_ratio: number;
  p50_cost_usd: number;
  p90_cost_usd: number;
  p50_input_tokens: number;
  p90_input_tokens: number;
  p50_output_tokens: number;
  p90_output_tokens: number;
}

export interface LlmCallEventDto {
  event_id: string;
  call_kind: string;
  iteration_index: number | null;
  tool_name: string | null;
  skill_name: string | null;
  provider: string;
  model_name: string;
  input_tokens: number;
  output_tokens: number;
  reasoning_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  total_cost_usd: number;
  duration_ms: number | null;
  created_at: string;
}

export interface MessageBreakdownResponse {
  message_id: string;
  events: LlmCallEventDto[];
  rollup: {
    input_tokens: number;
    output_tokens: number;
    reasoning_tokens: number;
    cache_read_tokens: number;
    cache_creation_tokens: number;
    total_cost_usd: number;
    iteration_count: number;
    tool_call_count: number;
  };
}

export default insightsService;
